import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { KeyRound, ShieldCheck } from 'lucide-react';
import { changePasswordSchema } from '@kick/validation';
import type { AuthSession } from '@kick/types';
import { api, getErrorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth-store';
import { Boton, Campo, Entrada } from '@/components/ui';
import { useAvisos } from '@/components/avisos';

/**
 * Cambio obligatorio de la contraseña temporal.
 *
 * Bloquea el panel entero hasta que se haga: una contraseña que un
 * administrador dictó por teléfono y que quizá quedó anotada en un papel no
 * puede seguir siendo la llave de un comercio.
 *
 * No hay forma de saltársela salvo cerrar sesión, y eso es a propósito.
 */
export function CambiarPassword() {
  const avisos = useAvisos();
  const { user, cerrar, iniciar, marcarPasswordCambiada } = useAuth();

  const [actual, setActual] = useState('');
  const [nueva, setNueva] = useState('');
  const [confirmacion, setConfirmacion] = useState('');
  const [error, setError] = useState<string | null>(null);

  const cambiar = useMutation({
    mutationFn: async () => {
      const { data } = await api.post<AuthSession>('/auth/cambiar-password', {
        currentPassword: actual,
        password: nueva,
        confirmPassword: confirmacion,
      });
      return data;
    },
    /**
     * Se adopta la sesión que devuelve el servidor.
     *
     * Cambiar la contraseña revoca todos los refresh, incluido el de esta
     * pestaña. Sin reemplazarlo, el panel seguiría funcionando con el token en
     * memoria y echaría al usuario en cuanto recargara.
     */
    onSuccess: (sesion) => {
      iniciar(sesion);
      marcarPasswordCambiada();
      avisos.exito('Contraseña actualizada');
    },
    onError: (e) => setError(getErrorMessage(e)),
  });

  const enviar = (): void => {
    setError(null);
    const revision = changePasswordSchema.safeParse({
      currentPassword: actual,
      password: nueva,
      confirmPassword: confirmacion,
    });

    if (!revision.success) {
      setError(revision.error.issues[0]?.message ?? 'Revisa los datos');
      return;
    }
    cambiar.mutate();
  };

  return (
    <div className="grid min-h-dvh place-items-center bg-kick-light px-4">
      <div className="w-full max-w-md">
        <div className="card-kick p-6">
          <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-kick-red/10 text-kick-red">
            <ShieldCheck className="h-6 w-6" />
          </div>

          <h1 className="mt-4 text-center text-2xl text-kick-dark">Cambia tu contraseña</h1>
          <p className="mx-auto mt-2 max-w-sm text-center text-sm text-kick-gray">
            Estás usando la contraseña temporal que te entregaron. Elige una propia para continuar.
          </p>

          <div className="mt-6 space-y-4">
            {/* Con id fijo: un campo de contraseña no expone rol accesible,
                así que es la forma estable de referenciarlo desde fuera. */}
            <Campo etiqueta="Contraseña temporal" requerido>
              <Entrada
                id="password-actual"
                type="password"
                autoComplete="current-password"
                value={actual}
                onChange={(e) => setActual(e.target.value)}
              />
            </Campo>

            <Campo
              etiqueta="Nueva contraseña"
              requerido
              ayuda="Mínimo 8 caracteres, con mayúscula, minúscula y número"
            >
              <Entrada
                id="password-nueva"
                type="password"
                autoComplete="new-password"
                value={nueva}
                onChange={(e) => setNueva(e.target.value)}
              />
            </Campo>

            <Campo etiqueta="Repite la nueva" requerido error={error ?? undefined}>
              <Entrada
                id="password-confirmacion"
                type="password"
                autoComplete="new-password"
                value={confirmacion}
                onChange={(e) => setConfirmacion(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && enviar()}
              />
            </Campo>
          </div>

          <Boton
            className="mt-6 w-full"
            icono={<KeyRound className="h-4 w-4" />}
            cargando={cambiar.isPending}
            onClick={enviar}
          >
            Guardar y continuar
          </Boton>
        </div>

        <button
          type="button"
          onClick={cerrar}
          className="mx-auto mt-4 block text-sm text-kick-gray transition hover:text-kick-ink"
        >
          Salir de la cuenta de {user?.username}
        </button>
      </div>
    </div>
  );
}
